import {
    AccountConnection,
    Banner,
    BlockStack,
    Box,
    Button,
    Card,
    Checkbox,
    InlineStack,
    SkeletonBodyText,
    Text,
    TextField,
} from "@shopify/polaris";
import { useCallback, useEffect, useState } from "react";
import agent from "../../app/api/agent";
import type { Turn14IntegrationSettings as Turn14IntegrationSettingsModel } from "../../app/models/Turn14Integration";

export type IntegrationEditorStatus = {
    hasChanges: boolean;
    loading: boolean;
    saving: boolean;
    save?: () => Promise<void>;
    discard?: () => void;
};

type Turn14IntegrationSettingsProps = {
    onStatusChange?: (status: IntegrationEditorStatus) => void;
};

const createEmptySettings = (): Turn14IntegrationSettingsModel => ({
    accountId: null,
    accountName: "",
    clientId: "",
    clientSecret: "",
    hasClientSecret: false,
    isConfigured: false,
    isActive: false,
    fitmentDataEnabled: false,
    fitmentDataAvailable: false,
});

const normalizeSettings = (settings?: Partial<Turn14IntegrationSettingsModel> | null): Turn14IntegrationSettingsModel => ({
    ...createEmptySettings(),
    ...(settings ?? {}),
    clientSecret: "",
});

const getConnectionDetails = (settings: Turn14IntegrationSettingsModel) => {
    if (!settings.isConfigured) {
        return "No account connected";
    }

    return settings.fitmentDataEnabled
        ? "Account connected. Fitment data enabled."
        : "Account connected. Fitment data disabled.";
};

const modalBackdropStyle = {
    alignItems: "center",
    background: "rgba(0, 0, 0, 0.36)",
    bottom: 0,
    display: "flex",
    justifyContent: "center",
    left: 0,
    padding: 24,
    position: "fixed",
    right: 0,
    top: 0,
    zIndex: 2147483647,
} as const;

const modalPanelStyle = {
    background: "white",
    borderRadius: 8,
    boxShadow: "0 24px 64px rgba(0, 0, 0, 0.28)",
    maxHeight: "calc(100vh - 48px)",
    maxWidth: 620,
    overflow: "auto",
    width: "100%",
} as const;

const Turn14IntegrationSettings: React.FC<Turn14IntegrationSettingsProps> = ({ onStatusChange }) => {
    const [settings, setSettings] = useState<Turn14IntegrationSettingsModel>();
    const [modalDraft, setModalDraft] = useState<Turn14IntegrationSettingsModel>();
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [pageMessage, setPageMessage] = useState<string>();
    const [error, setError] = useState<string>();
    const [success, setSuccess] = useState<string>();

    useEffect(() => {
        const loadSettings = async () => {
            setLoading(true);
            setError(undefined);
            setSuccess(undefined);
            setPageMessage(undefined);

            try {
                setSettings(normalizeSettings(await agent.Settings.turn14Integration()));
            } catch (err: any) {
                setError(err?.response?.data ?? err?.message ?? "Failed to load Turn14 integration settings.");
            } finally {
                setLoading(false);
            }
        };

        loadSettings();
    }, []);

    useEffect(() => {
        onStatusChange?.({
            // Credentials are saved inside the modal, not through the global
            // settings save bar. Keep parent save/discard state clean so future
            // integrations can follow the same compact-card pattern.
            hasChanges: false,
            loading,
            saving,
        });
    }, [loading, onStatusChange, saving]);

    const openModal = useCallback(() => {
        setModalDraft(normalizeSettings(settings));
        setError(undefined);
        setSuccess(undefined);
        setPageMessage(undefined);
        setModalOpen(true);
    }, [settings]);

    const closeModal = useCallback(() => {
        if (saving || testing) return;

        setModalOpen(false);
        setModalDraft(undefined);
        setError(undefined);
        setSuccess(undefined);
    }, [saving, testing]);

    const updateDraft = useCallback((changes: Partial<Turn14IntegrationSettingsModel>) => {
        setError(undefined);
        setSuccess(undefined);
        setModalDraft((current) => current ? ({ ...current, ...changes }) : current);
    }, []);

    const validate = useCallback(() => {
        if (!modalDraft) return "Turn14 settings are not loaded.";
        if (!modalDraft.clientId.trim()) return "Turn14 client ID is required.";

        // Saved secrets are intentionally write-only. A blank secret preserves
        // the stored value; new credentials need the secret before save/test.
        if (!modalDraft.clientSecret.trim() && !modalDraft.hasClientSecret) {
            return "Turn14 client secret is required.";
        }

        return undefined;
    }, [modalDraft]);

    const saveChanges = useCallback(async () => {
        const validationError = validate();
        if (validationError || !modalDraft) {
            setError(validationError);
            return;
        }

        setSaving(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const saved = normalizeSettings(await agent.Settings.updateTurn14Integration({
                ...modalDraft,
                accountName: "",
                clientId: modalDraft.clientId.trim(),
                clientSecret: modalDraft.clientSecret.trim(),
            }));
            setSettings(saved);
            setModalDraft(saved);
            setModalOpen(false);
            setPageMessage("Turn14 integration settings saved.");
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? "Failed to save Turn14 integration settings.");
        } finally {
            setSaving(false);
        }
    }, [modalDraft, validate]);

    const testConnection = useCallback(async () => {
        const validationError = validate();
        if (validationError || !modalDraft) {
            setError(validationError);
            return;
        }

        setTesting(true);
        setError(undefined);
        setSuccess(undefined);

        try {
            const result = await agent.Settings.testTurn14Integration({
                ...modalDraft,
                clientId: modalDraft.clientId.trim(),
                clientSecret: modalDraft.clientSecret.trim(),
            });

            if (result.success) {
                setSuccess(result.message || "Turn14 connection succeeded.");
            } else {
                setError(result.message || "Turn14 connection failed.");
            }
        } catch (err: any) {
            setError(err?.response?.data ?? err?.message ?? "Failed to test Turn14 connection.");
        } finally {
            setTesting(false);
        }
    }, [modalDraft, validate]);

    if (loading || !settings) {
        return (
            <Card>
                <Box padding="400">
                    <SkeletonBodyText lines={4} />
                </Box>
            </Card>
        );
    }

    return (
        <BlockStack gap="400">
            {pageMessage ? (
                <Banner tone="success">
                    <Text as="p">{pageMessage}</Text>
                </Banner>
            ) : null}

            <AccountConnection
                accountName={settings.isConfigured ? "Turn14 connected" : ""}
                connected={settings.isConfigured}
                title="Turn14"
                details={getConnectionDetails(settings)}
                action={{
                    content: settings.isConfigured ? "Manage" : "Connect",
                    onAction: openModal,
                }}
                termsOfService={(
                    <Text as="p" variant="bodyMd" tone="subdued">
                        Connect Turn14 credentials for fitment reference data. Product catalogue and inventory can be added later.
                    </Text>
                )}
            />

            {modalOpen ? (
                <div style={modalBackdropStyle} role="presentation">
                    <section
                        aria-modal="true"
                        aria-labelledby="turn14-integration-settings-title"
                        role="dialog"
                        style={modalPanelStyle}
                    >
                        {modalDraft ? (
                            <>
                                <Box padding="400" borderBlockEndWidth="025" borderColor="border">
                                    <Text as="h2" variant="headingMd" id="turn14-integration-settings-title">
                                        Turn14
                                    </Text>
                                </Box>
                                <Box padding="400">
                                    <BlockStack gap="400">
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            Enter Turn14 API credentials for fitment data only. This does not push products, inventory, or fitment changes to Shopify.
                                        </Text>

                                            {error ? (
                                                <Banner tone="critical">
                                                    <Text as="p">{error}</Text>
                                                </Banner>
                                            ) : null}
                                            {success ? (
                                                <Banner tone="success">
                                                    <Text as="p">{success}</Text>
                                                </Banner>
                                            ) : null}

                                            <TextField
                                                label="Client ID"
                                                autoComplete="off"
                                                maxLength={100}
                                                value={modalDraft.clientId}
                                                onChange={(value) => updateDraft({ clientId: value })}
                                            />
                                            <TextField
                                                label="Client secret"
                                                autoComplete="new-password"
                                                type="password"
                                                maxLength={200}
                                                value={modalDraft.clientSecret}
                                                placeholder={modalDraft.hasClientSecret ? "Saved credential" : undefined}
                                                onChange={(value) => updateDraft({ clientSecret: value })}
                                                helpText={modalDraft.hasClientSecret ? "Leave blank to keep the saved secret." : undefined}
                                            />
                                            <BlockStack gap="100">
                                                <Checkbox
                                                    label="Use Turn14 fitment reference data"
                                                    checked={modalDraft.fitmentDataEnabled}
                                                    onChange={(checked) => updateDraft({ fitmentDataEnabled: checked })}
                                                />
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    Turn14 fitment data is read as a reference source for this store after the sync service imports matching data.
                                                </Text>
                                            </BlockStack>
                                    </BlockStack>
                                </Box>
                                <Box padding="400" borderBlockStartWidth="025" borderColor="border">
                                    <InlineStack align="end" gap="200">
                                        <Button onClick={closeModal} disabled={saving || testing}>
                                            Cancel
                                        </Button>
                                        <Button onClick={testConnection} loading={testing} disabled={saving || testing}>
                                            {testing ? "Testing..." : "Test connection"}
                                        </Button>
                                        <Button
                                            variant="primary"
                                            onClick={saveChanges}
                                            loading={saving}
                                            disabled={saving || testing}
                                        >
                                            {saving ? "Saving..." : "Save"}
                                        </Button>
                                    </InlineStack>
                                </Box>
                            </>
                        ) : null}
                    </section>
                </div>
            ) : null}
        </BlockStack>
    );
};

export default Turn14IntegrationSettings;
